import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Pencil, Copy, Trash2, Download, Upload, X, Save } from "lucide-react";

const EMPTY = { category: "Citations", theme: "", title: "", author: "", content: "",
  explanation: "", context: "", date: "", source: "", tags: "", published: true, publish_at: "" };

export default function AdminKnowledge() {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [form, setForm] = useState(null); // null = list, object = editing/creating
  const [editingId, setEditingId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/knowledge", { params: { q: q || undefined, category: cat || undefined } });
      setEntries(data.entries); setCategories(data.categories);
    } catch (e) { toast.error(apiErr(e)); }
  }, [q, cat]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm({ ...EMPTY }); };
  const openEdit = (e) => {
    setEditingId(e.entry_id);
    setForm({ ...EMPTY, ...e, tags: (e.tags || []).join(", "), publish_at: e.publish_at ? e.publish_at.slice(0, 16) : "" });
  };
  const save = async () => {
    if (!form.title || !form.category) return toast.error("Titre et catégorie requis.");
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : null };
    try {
      if (editingId) await api.put(`/admin/knowledge/${editingId}`, payload);
      else await api.post("/admin/knowledge", payload);
      toast.success(editingId ? "Fiche mise à jour." : "Fiche créée.");
      setForm(null); setEditingId(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const duplicate = async (id) => { try { await api.post(`/admin/knowledge/${id}/duplicate`); toast.success("Fiche dupliquée (brouillon)."); load(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async (id) => { if (!window.confirm("Supprimer définitivement cette fiche ?")) return; try { await api.delete(`/admin/knowledge/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const exportJson = async () => {
    try {
      const { data } = await api.get("/admin/knowledge-export");
      const blob = new Blob([JSON.stringify(data.entries, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "bizarre-bibliotheque.json"; a.click();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const doImport = async () => {
    try {
      const parsed = JSON.parse(importText);
      const arr = Array.isArray(parsed) ? parsed : parsed.entries;
      const { data } = await api.post("/admin/knowledge-import", { entries: arr });
      toast.success(`${data.imported} fiche(s) importée(s).`); setShowImport(false); setImportText(""); load();
    } catch (e) { toast.error("JSON invalide ou import échoué."); }
  };

  const Field = ({ label, children }) => (
    <label className="block"><span className="text-white/60 text-xs mb-1 block">{label}</span>{children}</label>
  );
  const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm";

  if (form) {
    return (
      <div className="card-surface p-5 max-w-3xl" data-testid="kb-admin-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg">{editingId ? "Modifier la fiche" : "Nouvelle fiche"}</h3>
          <button onClick={() => setForm(null)} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Field label="Catégorie"><select data-testid="kb-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select></Field>
          <Field label="Thème"><input data-testid="kb-form-theme" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className={inputCls} /></Field>
          <Field label="Titre"><input data-testid="kb-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} /></Field>
          <Field label="Auteur / personnalité"><input data-testid="kb-form-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputCls} /></Field>
        </div>
        <Field label="Contenu (pour une citation : la citation)"><textarea data-testid="kb-form-content" rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={`${inputCls} resize-none mb-3`} /></Field>
        <Field label="Explication pédagogique"><textarea data-testid="kb-form-explanation" rows={2} value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} className={`${inputCls} resize-none mb-3`} /></Field>
        <Field label="Contexte historique"><textarea rows={2} value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} className={`${inputCls} resize-none mb-3`} /></Field>
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <Field label="Date"><input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} /></Field>
          <Field label="Source"><input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={inputCls} /></Field>
          <Field label="Mots-clés (séparés par ,)"><input data-testid="kb-form-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inputCls} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-4 items-end">
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" data-testid="kb-form-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publié
          </label>
          <Field label="Programmer (optionnel)"><input type="datetime-local" data-testid="kb-form-publishat" value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} className={inputCls} /></Field>
        </div>
        <button data-testid="kb-form-save" onClick={save} className="gold-gradient text-black rounded-full px-6 py-2.5 text-sm font-medium inline-flex items-center gap-2"><Save size={16} /> Enregistrer</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-xl px-3">
          <Search size={16} className="text-white/40" />
          <input data-testid="kb-admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une fiche..." className="flex-1 bg-transparent py-2 outline-none text-sm" />
        </div>
        <select data-testid="kb-admin-catfilter" value={cat} onChange={(e) => setCat(e.target.value)} className="bg-white/[0.06] border border-white/15 rounded-xl px-3 py-2 text-sm outline-none">
          <option value="">Toutes catégories</option>
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <button data-testid="kb-admin-new" onClick={openCreate} className="gold-gradient text-black rounded-xl px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5"><Plus size={16} /> Nouvelle</button>
        <button data-testid="kb-admin-export" onClick={exportJson} className="border border-white/12 rounded-xl px-3 py-2 text-sm text-white/70 inline-flex items-center gap-1.5"><Download size={15} /> Exporter</button>
        <button data-testid="kb-admin-import" onClick={() => setShowImport(true)} className="border border-white/12 rounded-xl px-3 py-2 text-sm text-white/70 inline-flex items-center gap-1.5"><Upload size={15} /> Importer</button>
      </div>

      {showImport && (
        <div className="card-surface p-4" data-testid="kb-import-panel">
          <p className="text-white/60 text-sm mb-2">Collez un tableau JSON de fiches (mêmes champs que l'export).</p>
          <textarea value={importText} onChange={(e) => setImportText(e.target.value)} rows={5} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-xs font-mono mb-2" placeholder='[{"category":"Citations","title":"...","content":"..."}]' />
          <div className="flex gap-2">
            <button data-testid="kb-import-confirm" onClick={doImport} className="gold-gradient text-black rounded-full px-4 py-2 text-sm font-medium">Importer</button>
            <button onClick={() => setShowImport(false)} className="text-white/50 text-sm px-3">Annuler</button>
          </div>
        </div>
      )}

      <p className="text-white/40 text-xs">{entries.length} fiche(s)</p>
      <div className="space-y-2">
        {entries.map((e) => {
          const scheduled = e.publish_at && new Date(e.publish_at) > new Date();
          const status = !e.published ? { t: "Brouillon", c: "bg-white/10 text-white/50" } : scheduled ? { t: "Programmée", c: "bg-blue-500/15 text-blue-300" } : { t: "Publié", c: "bg-gold/15 text-gold" };
          return (
            <div key={e.entry_id} data-testid={`kb-admin-row-${e.entry_id}`} className="card-surface p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm truncate"><span className="text-white/40 text-xs mr-2">{e.category}</span>{e.title}</p>
                <p className="text-white/35 text-xs mt-0.5 flex items-center gap-2"><span className={`px-2 py-0.5 rounded-full ${status.c}`}>{status.t}</span>{e.author || e.theme}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`kb-edit-${e.entry_id}`} onClick={() => openEdit(e)} className="text-white/50 hover:text-gold"><Pencil size={16} /></button>
                <button data-testid={`kb-dup-${e.entry_id}`} onClick={() => duplicate(e.entry_id)} className="text-white/50 hover:text-gold"><Copy size={16} /></button>
                <button data-testid={`kb-del-${e.entry_id}`} onClick={() => del(e.entry_id)} className="text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
          );
        })}
        {entries.length === 0 && <p className="text-white/30 text-sm">Aucune fiche.</p>}
      </div>
    </div>
  );
}
